import { alertRequiresNavAttention, getAlertPageKey } from '@aloft/toolkit-shared';

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { routeToStepMap } from '../../../../hooks/validation/consts.ts';
import { useAppraisifyNavigation } from '../../../../hooks/validation/useAppraisifyNavigation.ts';
import { selectCommentaryV2 } from '../../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsInspectifyOrder } from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectTriggeredAlerts } from '../../../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { useAlerts } from './useAlerts.tsx';

export const useActiveAlertCount = (): number => {
  const alerts = useAlerts();
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const triggeredAlertTypes = useAppSelector(selectTriggeredAlerts);
  const triggered = useMemo(() => new Set(triggeredAlertTypes), [triggeredAlertTypes]);
  const { pathname } = useLocation();
  const { getCurrentAppraisifyRoute, isRouteWithinAlertNavFrontier } = useAppraisifyNavigation();
  const currentAppraisifyRoute = getCurrentAppraisifyRoute(pathname);

  return useMemo(() => {
    const requiringAttention = Object.values(alerts)
      .flat()
      .filter((alert) => alertRequiresNavAttention(alert, alertsCommentary));

    if (isInspectifyOrder) {
      return requiringAttention.filter((alert) => triggered.has(alert.type)).length;
    }

    return requiringAttention.filter((alert) => {
      const page = getAlertPageKey(alert, routeToStepMap);
      if (!currentAppraisifyRoute) {
        return false;
      }
      return isRouteWithinAlertNavFrontier(page, currentAppraisifyRoute, alerts);
    }).length;
  }, [
    alerts,
    alertsCommentary,
    currentAppraisifyRoute,
    isInspectifyOrder,
    isRouteWithinAlertNavFrontier,
    triggered,
  ]);
};
